import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  ClockCircleOutlined,
  CloudSyncOutlined,
  SafetyCertificateOutlined,
  DatabaseOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';

/* Single tooltip look everywhere — readable on light and dark app chrome */
const TIP = {
  card: {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    boxShadow: '0 12px 40px rgba(15, 23, 42, 0.14), 0 2px 8px rgba(15, 23, 42, 0.06)',
  },
  header: {
    background: 'linear-gradient(135deg, #eff6ff 0%, #f5f3ff 55%, #fdf2f8 100%)',
    borderBottom: '1px solid #e2e8f0',
  },
  title: '#0f172a',
  body: '#475569',
  sectionLabel: '#94a3b8',
  sectionText: '#64748b',
  monoName: '#334155',
  monoMuted: '#64748b',
  note: { background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e' },
  noteIcon: '#d97706',
  helpBorder: '#cbd5e1',
  helpBg: '#f1f5f9',
  helpColor: '#64748b',
  helpHoverBorder: '#93c5fd',
  helpHoverBg: '#eff6ff',
  helpHoverColor: '#2563eb',
};

/* ══════════════════════════════════════════════════════════════════════
   TOOLTIP COPY — every value, option, min/max comes from actual code
   ══════════════════════════════════════════════════════════════════════ */
const TIPS = {
  // ── Project Details ──
  'Group':            { what: 'Assigns this project to an organisational group for access control and billing separation.', values: 'Any existing group in your organisation', impact: 'Changing group may alter who can view or edit this project.' },
  'Project Name':     { what: 'Display name shown in the sidebar, logs, and API references.', values: '3–60 characters, alphanumeric and spaces' },
  'Description':      {
    what: 'Explains what this project is about. The AI assistant uses this as project context so answers match your domain, audience, and scope.',
    values: 'Required, 10–500 characters. Cover: what the project is (domain/topic); who it is for (audience); what it should and should not do (scope, tone, limits).',
    impact: 'Without a description, the assistant has no project-level context beyond retrieved documents.',
  },
  'Project ID':       { what: 'Unique system identifier used in API calls and webhook URLs. Cannot be changed after creation.' },
  'Created By':       { what: 'The user who originally created this project.' },
  'Created At':       { what: 'Timestamp when this project was first created.' },
  'Updated At':       { what: 'Timestamp of the most recent configuration change.' },
  'Document Count':   { what: 'Number of source documents currently indexed in this project\'s vector store.' },
  'Chunk Count':      { what: 'Total text chunks stored in the vector store available for retrieval.' },
  'Embedding Status': { what: 'Current state of the embedding pipeline. Green means ready to query.' },
  'Vector Status':    { what: 'Current state of the vector store. Non-ready state means retrieval may return incomplete results.' },
  'Status':           { what: 'Overall project health combining embedding and vector status.' },

  // ── Embedding ──
  'Embedding Model':  { what: 'The provider used to generate vector embeddings from text chunks.', values: 'openai · bge', impact: 'Changing provider requires full re-ingestion — existing embeddings become incompatible.' },
  'Model Name':       { what: 'The specific embedding model within the selected provider.', values: 'OpenAI: text-embedding-3-small · BGE: BAAI/bge-base-en-v1.5 · BAAI/bge-large-en-v1.5 · BAAI/bge-m3', impact: 'Changing model invalidates all existing embeddings — full re-index required.' },

  // ── Vector Store ──
  'Vector Store Type':         { what: 'The database engine storing all embeddings for this project. Currently fixed to Milvus.' },
  'Collection Name':           { what: 'The specific Milvus collection where this project\'s vectors are stored. Must be unique across all projects.' },
  'Per-file vector retention':  { what: 'When enabled, deleting a source file also removes its chunks from the vector store automatically.' },

  // ── Knowledge Source ──
  'S3 Bucket': { what: 'The AWS S3 bucket this project ingests documents from. Configured at project creation time.' },
  'S3 Region': { what: 'AWS region where the S3 bucket is hosted. Must match the bucket\'s actual region to avoid access errors.' },

  // ── Graph RAG ──
  'Enable Graph RAG':  { what: 'Activates knowledge graph augmentation alongside standard vector search. Improves answers about entity relationships.', values: 'on / off', impact: 'Enabling requires a running GraphRAG backend and triggers full graph ingestion.' },
  'Ingest Endpoint':   { what: 'URL of the GraphRAG backend that processes and stores entity relationships extracted from your documents.', values: 'Valid HTTPS URL', impact: 'Wrong endpoint will silently fail graph ingestion.' },
  'Group ID':          { what: 'Numeric identifier scoping graph data to this project within the shared GraphRAG backend.', values: '1–9999, must be unique per project', impact: 'Duplicate Group ID will mix this project\'s graph data with another.' },
  'Timeout (seconds)': { what: 'How long to wait for the GraphRAG ingest endpoint before marking the request as failed.', values: '30–900 seconds, default 300', impact: 'Too low on large documents causes incomplete graph ingestion.' },
  'Timeout':           { what: 'GraphRAG ingest timeout in seconds.' },
  'Enabled':           { what: 'Whether Graph RAG knowledge graph augmentation is active for this project.' },

  // ── Retriever ──
  'Search Type':       { what: 'How the system searches for relevant information.' },
  'Top K':             { what: 'Number of results used to answer a question.' },
  'MMR Fetch K':       { what: 'Number of results reviewed before selecting the best ones.' },
  'MMR Lambda':        { what: 'Balance between relevance and variety in results.' },
  'BM25 K':            { what: 'Number of keyword matches considered.' },
  'Vector K':          { what: 'Number of context-based matches considered.' },
  'Hybrid Alpha':      { what: 'Balance between keyword search and AI-powered semantic search.' },
  'Cosine Similarity Threshold': { what: 'Minimum similarity score for a chunk to be included in results. Filters out low-relevance noise.', values: '0.0–1.0, step 0.01' },
  'Use Cosine Similarity':       { what: 'When enabled, applies the cosine similarity threshold filter to retrieval results.' },

  // ── LLM & Guardrails ──
  'LLM Provider':      { what: 'The AI provider whose models generate answers from retrieved chunks. Determines which models are available.', values: 'openai · bedrock · ollama · qwen · vllm', impact: 'Switching provider changes available models, pricing, and response formatting.' },
  'LLM Model':         { what: 'The specific LLM model used to generate responses.' },
  'Model':             { what: 'The specific LLM used to generate responses. Larger models produce better answers at higher cost and latency.', values: 'Depends on provider (e.g. gpt-4o-mini for OpenAI)', impact: 'Different models have different context window limits.' },
  'Temperature':       { what: 'Controls randomness in the LLM output. 0.0 = deterministic and factual, 1.0+ = creative and varied.', values: '0.0–2.0, step 0.1, recommended 0.3–0.7 for RAG', impact: 'High temperature increases hallucination risk in RAG pipelines.' },
  'Enable Guardrails': { what: 'Activates real-time PII detection and content safety scanning on user input and LLM output.', values: 'on / off', impact: 'Disabling exposes raw PII in queries and responses. Enabling adds 10–30ms latency per query.' },
  'Guardrails Enabled': { what: 'Whether guardrails content safety scanning is active for this project.' },
  'Select Guards':     { what: 'Choose which guardrail validators to apply at this stage of the pipeline.', values: 'DetectPII · RestrictToTopic · NSFWText' },

  // ── Guard section headers ──
  'Input Guards':                 { what: 'Scan and sanitize the user\'s query before it reaches the LLM. Prevents sensitive data entering the AI pipeline.' },
  'Pre-Embedding Guards':         { what: 'Applied to documents before chunking and indexing. Ensures PII is not stored inside vector embeddings.', impact: 'Only applies to newly ingested documents — existing indexed chunks are not retroactively cleaned.' },
  'Context (Retrieval) Guards':   { what: 'Applied to each retrieved chunk before it enters the LLM prompt. Removes or redacts PII from context so it cannot influence the generated answer.' },
  'Output Guards':                { what: 'Applied to the LLM response before showing to the user. Last line of defence — masks any PII that slipped through earlier stages.' },

  // ── Guard sub-fields ──
  'Threshold':            { what: 'Minimum confidence score for the NSFW model to flag content as violating. Lower = more aggressive, higher = more lenient.', values: '0.0–1.0, step 0.1, default 0.8', impact: 'Too low causes false positives and over-blocking of legitimate content.' },
  'Confidence Threshold': { what: 'Minimum confidence for PII entity detection. Entities scoring below this are ignored.', values: '0.0–1.0, step 0.1, default 0.7', impact: 'Too low flags currency codes and technical terms as PII.' },
  'Validation Method':    { what: 'Whether the guard evaluates each sentence independently or the full text as one unit.', values: 'sentence (more precise) · full (faster)' },
  'PII Entities':         { what: 'Which categories of personally identifiable information this guard should detect and mask.', values: 'EMAIL_ADDRESS · PHONE_NUMBER · CREDIT_CARD · SSN · IP_ADDRESS · PERSON · LOCATION · DATE_TIME' },
  'Valid Topics':         { what: 'Topics this RAG is allowed to answer about. Queries outside these topics will be rejected by the guard.', impact: 'Overly narrow topics may block legitimate queries.' },
  'Invalid Topics':       { what: 'Topics this RAG must never respond to regardless of context. Acts as an explicit blocklist.', impact: 'Takes priority over Valid Topics if there is overlap.' },
  'Disable LLM':          { what: 'Turns off the LLM-based topic classification within this guard. Uses only the classifier model instead.', values: 'on / off, default off' },
  'Disable Classifier':   { what: 'Turns off the classifier-based topic detection. Uses only the LLM for classification instead.', values: 'on / off, default on' },
};

const ON_FAIL_OPTIONS = [
  { name: 'noop',      desc: 'log only, response shown unchanged' },
  { name: 'fix',       desc: 'mask or correct the violation in-place' },
  { name: 'filter',    desc: 'strip the violating content from response' },
  { name: 'refrain',   desc: 'replace entire response with a refusal message' },
  { name: 'exception', desc: 'throw error and halt the pipeline' },
  { name: 'reask',     desc: 'send back to LLM with correction instructions' },
  { name: 'fix_reask', desc: 'fix in-place, then reask if still failing' },
];

TIPS['On Fail Policy'] = {
  what: 'What the system does when this guard detects a violation.',
  values: '__ON_FAIL__',
  impact: 'exception and refrain are aggressive — use noop or fix for production RAG to avoid breaking user experience.',
};

TIPS['RAG pipeline schedule'] = {
  variant: 'pipeline-schedule',
  what: 'Automatically processes files from your project S3 bucket on a repeating timer.',
  steps: [
    { icon: CloudSyncOutlined, color: '#2563eb', label: 'Sync', detail: 'Pick up new uploads, updates, and deletes' },
    { icon: SafetyCertificateOutlined, color: '#7c3aed', label: 'Process', detail: 'Guardrails, chunking, and embeddings' },
    { icon: DatabaseOutlined, color: '#059669', label: 'Index', detail: 'Push vectors so chat and search stay current' },
  ],
  values: 'Set Every + Unit, then Save schedule. Last run and Next run appear below.',
  impact: 'Works alongside S3 event triggers and Run now. Shorter intervals clear backlog faster; only one run per project at a time.',
};

const ImpactNote = ({ text }) => (
  <div
    style={{
      marginTop: 8,
      padding: '8px 10px',
      borderRadius: 8,
      display: 'flex',
      gap: 8,
      alignItems: 'flex-start',
      ...TIP.note,
    }}
  >
    <InfoCircleOutlined style={{ fontSize: 12, color: TIP.noteIcon, marginTop: 2, flexShrink: 0 }} />
    <span style={{ fontSize: 11, lineHeight: 1.5 }}>{text}</span>
  </div>
);

const StandardTipBody = ({ tip }) => {
  const isOnFail = tip.values === '__ON_FAIL__';

  if (tip.messageOnly && tip.what) {
    return (
      <div style={{ padding: '10px 12px', fontSize: 12, color: TIP.body, lineHeight: 1.55 }}>
        {tip.what}
      </div>
    );
  }

  return (
    <div style={{ padding: '12px 14px' }}>
      {tip.name ? (
        <div style={{ fontSize: 13, fontWeight: 600, color: TIP.title, lineHeight: 1.3 }}>{tip.name}</div>
      ) : null}
      <div style={{ fontSize: 12, color: TIP.body, lineHeight: 1.55, marginTop: tip.name ? 6 : 0 }}>{tip.what}</div>

      {tip.values && !isOnFail ? (
        <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: TIP.sectionLabel, marginBottom: 4 }}>
            Values
          </div>
          <div style={{ fontSize: 11, color: TIP.sectionText, lineHeight: 1.5 }}>{tip.values}</div>
        </div>
      ) : null}

      {isOnFail ? (
        <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: TIP.sectionLabel, marginBottom: 6 }}>
            Values
          </div>
          {ON_FAIL_OPTIONS.map((opt) => (
            <div key={opt.name} style={{ display: 'flex', gap: 6, marginBottom: 4, fontSize: 11, lineHeight: 1.45 }}>
              <span style={{ color: TIP.monoName, fontFamily: '"JetBrains Mono", ui-monospace, monospace', flexShrink: 0 }}>{opt.name}</span>
              <span style={{ color: TIP.sectionLabel }}>—</span>
              <span style={{ color: TIP.sectionText }}>{opt.desc}</span>
            </div>
          ))}
        </div>
      ) : null}

      {tip.impact ? <ImpactNote text={tip.impact} /> : null}
    </div>
  );
};

const PipelineScheduleTipBody = ({ tip }) => {
  const steps = tip.steps || [];

  return (
    <>
      <div style={{ padding: '12px 14px', ...TIP.header }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: 8,
              background: 'rgba(37, 99, 235, 0.12)',
              color: '#2563eb',
              fontSize: 15,
            }}
          >
            <ClockCircleOutlined />
          </span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: TIP.title, lineHeight: 1.3 }}>{tip.name}</div>
            <div style={{ fontSize: 11, color: TIP.body, lineHeight: 1.45, marginTop: 2 }}>{tip.what}</div>
          </div>
        </div>
      </div>
      <div style={{ padding: '12px 14px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {steps.map((step) => {
            const Icon = step.icon;
            return (
              <div key={step.label} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 26,
                    height: 26,
                    borderRadius: 7,
                    background: `${step.color}18`,
                    color: step.color,
                    fontSize: 13,
                    flexShrink: 0,
                  }}
                >
                  <Icon />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: TIP.title }}>{step.label}</div>
                  <div style={{ fontSize: 10, color: TIP.body, lineHeight: 1.45, marginTop: 1 }}>{step.detail}</div>
                </div>
              </div>
            );
          })}
        </div>
        {tip.values ? (
          <div style={{ marginTop: 12, padding: '8px 10px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: TIP.sectionLabel, marginBottom: 4 }}>
              How to use
            </div>
            <div style={{ fontSize: 11, color: TIP.sectionText, lineHeight: 1.5 }}>{tip.values}</div>
          </div>
        ) : null}
        {tip.impact ? <ImpactNote text={tip.impact} /> : null}
      </div>
    </>
  );
};

const TooltipPortal = ({ anchor, tip }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const reposition = () => {
      if (!anchor || !ref.current) return;
      const rect = anchor.getBoundingClientRect();
      const el = ref.current;
      const tw = el.offsetWidth || (tip?.variant === 'pipeline-schedule' ? 300 : 280);
      const th = el.offsetHeight || 150;
      const minSide = tw + 20;
      const spaceRight = window.innerWidth - rect.right - 16;
      const spaceLeft = rect.left - 16;

      let top;
      let left;
      if (spaceRight >= minSide) {
        left = rect.right + 10;
        top = rect.top + rect.height / 2 - th / 2;
      } else if (spaceLeft >= minSide) {
        left = rect.left - tw - 10;
        top = rect.top + rect.height / 2 - th / 2;
      } else {
        left = rect.left + rect.width / 2 - tw / 2;
        top = rect.bottom + 8;
      }
      top = Math.max(8, Math.min(top, window.innerHeight - th - 8));
      left = Math.max(8, Math.min(left, window.innerWidth - tw - 8));
      setPos({ top, left });
    };
    reposition();
    window.addEventListener('resize', reposition);
    return () => window.removeEventListener('resize', reposition);
  }, [anchor, tip?.variant]);

  if (!tip) return null;

  const isRichPipeline = tip.variant === 'pipeline-schedule';

  return ReactDOM.createPortal(
    <div
      ref={ref}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        zIndex: 9999,
        maxWidth: isRichPipeline ? 300 : 288,
        width: isRichPipeline ? 300 : undefined,
        borderRadius: 12,
        overflow: 'hidden',
        animation: 'ttFadeIn 0.15s ease',
        pointerEvents: 'none',
        padding: 0,
        ...TIP.card,
      }}
    >
      <style>{`@keyframes ttFadeIn{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}`}</style>
      {isRichPipeline ? <PipelineScheduleTipBody tip={tip} /> : <StandardTipBody tip={tip} />}
    </div>,
    document.body
  );
};

function resolveTip(tipKey, tooltipProp) {
  const catalogTip = TIPS[tipKey];
  if (catalogTip) return catalogTip;
  if (tooltipProp && typeof tooltipProp === 'string') {
    return { what: tooltipProp };
  }
  return null;
}

export const FieldLabel = ({ label, tooltip, guardName }) => {
  const [show, setShow] = useState(false);
  const wrapRef = useRef(null);
  const tipKey = tooltip || label;
  const tip = resolveTip(tipKey, tooltip);
  const displayName = guardName ? `${guardName} — ${label}` : label;
  const messageOnly = !tip?.values && !tip?.impact && !tip?.variant;

  if (!tip) {
    return <span>{label}</span>;
  }

  return (
    <span
      ref={wrapRef}
      className="field-label-wrap"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onFocus={() => setShow(true)}
      onBlur={() => setShow(false)}
      tabIndex={0}
      role="button"
      aria-label={`${label}. Help: ${tip.what || displayName}`}
    >
      <span className="field-label-text">{label}</span>
      {show && wrapRef.current ? (
        <TooltipPortal
          anchor={wrapRef.current}
          tip={{
            ...tip,
            name: messageOnly ? undefined : displayName,
            messageOnly,
          }}
        />
      ) : null}
    </span>
  );
};
